import { useState, useEffect } from "react";
import { CheckSquare, Plus, RefreshCw, Trash, Edit, Check } from "lucide-react";
import { cn } from "../../utils/tailwindUtils";
import { ContainerCard } from "../../components/ui/cards";
import { DialogModal, ConfirmPopupModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import { Select, DatePicker } from "antd";
import useApiMethods from "../../lib/useApiMethods";
import { TASKS_URL, USERS_SIMPLE_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import dayjs from "dayjs";

const PRIORITY_COLORS = {
    High:   'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    Medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    Low:    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};

const STATUS_COLORS = {
    Pending:     'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    'In Progress': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    Completed:   'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};

const DEFAULT_FORM = {
    id: null,
    title: '',
    description: '',
    assigned_to_id: null,
    due_date: null,
    priority: 'Medium',
    task_status: 'Pending',
};

const TasksList = () => {
    const { fetch, create, update, remove } = useApiMethods();
    const [tasks, setTasks] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [formData, setFormData] = useState(DEFAULT_FORM);
    const [errors, setErrors] = useState({});
    const [statusFilter, setStatusFilter] = useState('Pending');

    const loadTasks = () => {
        setLoading(true);
        fetch(TASKS_URL).then(res => {
            if (res?.status === 'success') setTasks(res.data || []);
        }).finally(() => setLoading(false));
    };

    useEffect(() => {
        loadTasks();
        fetch(USERS_SIMPLE_URL).then(res => {
            if (res?.status === 'success') setUsers(res.data || []);
        });
    }, []);

    const resetForm = () => {
        setFormData(DEFAULT_FORM);
        setErrors({});
        setOpenDialog(false);
        setEditMode(false);
    };

    const validate = () => {
        const e = {};
        if (!formData.title.trim()) e.title = 'Title is required';
        if (!formData.assigned_to_id) e.assigned_to_id = 'Assignee is required';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        setSaving(true);
        const payload = {
            ...formData,
            due_date: formData.due_date ? dayjs(formData.due_date).format('YYYY-MM-DD') : null,
        };
        const res = editMode
            ? await update(TASKS_URL, payload)
            : await create(TASKS_URL, payload);

        if (res?.status === 'success') {
            ToastMsg('success', res.message || `Task ${editMode ? 'updated' : 'created'} successfully`);
            resetForm();
            loadTasks();
        } else {
            ToastMsg('error', res?.message || 'Something went wrong');
        }
        setSaving(false);
    };

    const handleDelete = async () => {
        const res = await remove(`${TASKS_URL}?id=${deleteConfirm}`);
        if (res?.status === 'success') {
            ToastMsg('success', 'Task deleted');
            setTasks(prev => prev.filter(t => t.id !== deleteConfirm));
        } else {
            ToastMsg('error', res?.message || 'Failed to delete');
        }
        setDeleteConfirm(null);
    };

    const handleStatusToggle = async (task) => {
        const nextStatus = task.task_status === 'Completed' ? 'Pending' : 'Completed';
        const res = await update(TASKS_URL, { id: task.id, task_status: nextStatus });
        if (res?.status === 'success') {
            setTasks(prev => prev.map(t => t.id === task.id ? { ...t, task_status: nextStatus } : t));
        }
    };

    const openEdit = (task) => {
        setFormData({
            id: task.id,
            title: task.title,
            description: task.description || '',
            assigned_to_id: task.assigned_to_id,
            due_date: task.due_date ? dayjs(task.due_date) : null,
            priority: task.priority,
            task_status: task.task_status,
        });
        setEditMode(true);
        setOpenDialog(true);
    };

    const filtered = statusFilter === 'All' ? tasks : tasks.filter(t => t.task_status === statusFilter);

    return (
        <ContainerCard>
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white">Tasks</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Manage and track all tasks</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={loadTasks}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors">
                        <RefreshCw className="h-4 w-4" />
                        Refresh
                    </button>
                    <button onClick={() => setOpenDialog(true)}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors">
                        <Plus className="h-4 w-4" />
                        Add Task
                    </button>
                </div>
            </div>

            {/* Filter tabs */}
            <div className="flex items-center gap-1 mb-4">
                {['All', 'Pending', 'In Progress', 'Completed'].map(s => (
                    <button key={s} onClick={() => setStatusFilter(s)}
                        className={cn("px-3 py-1.5 text-xs font-medium rounded-lg transition-colors",
                            statusFilter === s
                                ? "bg-blue-600 text-white"
                                : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                        )}>
                        {s} ({s === 'All' ? tasks.length : tasks.filter(t => t.task_status === s).length})
                    </button>
                ))}
            </div>

            {/* Task list */}
            {loading ? (
                <div className="text-center py-12 text-gray-400 text-sm">Loading tasks...</div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-12">
                    <CheckSquare className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">No tasks found</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {filtered.map(task => (
                        <div key={task.id}
                            className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-blue-300 dark:hover:border-blue-700 transition-colors">
                            <button onClick={() => handleStatusToggle(task)}
                                className={cn("flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors",
                                    task.task_status === 'Completed'
                                        ? "bg-green-500 border-green-500 text-white"
                                        : "border-gray-300 dark:border-gray-600 hover:border-blue-500"
                                )}>
                                {task.task_status === 'Completed' && <Check className="h-3 w-3" />}
                            </button>

                            <div className="flex-1 min-w-0">
                                <p className={cn("text-sm font-medium text-gray-900 dark:text-white truncate",
                                    task.task_status === 'Completed' && "line-through text-gray-400 dark:text-gray-500")}>
                                    {task.title}
                                </p>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    {task.assigned_to_name && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400">→ {task.assigned_to_name}</span>
                                    )}
                                    {task.due_date && (
                                        <span className="text-xs text-gray-400 dark:text-gray-500">{dayjs(task.due_date).format('DD MMM YYYY')}</span>
                                    )}
                                    {task.customer_name && (
                                        <span className="text-xs text-blue-500">#{task.customer_name}</span>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center gap-2 flex-shrink-0">
                                <span className={cn("px-2 py-0.5 text-xs font-medium rounded-full", PRIORITY_COLORS[task.priority])}>
                                    {task.priority}
                                </span>
                                <span className={cn("px-2 py-0.5 text-xs font-medium rounded-full", STATUS_COLORS[task.task_status])}>
                                    {task.task_status}
                                </span>
                                <button onClick={() => openEdit(task)}
                                    className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-700 rounded-lg">
                                    <Edit className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => setDeleteConfirm(task.id)}
                                    className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg">
                                    <Trash className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create / Edit Dialog */}
            <DialogModal
                open={openDialog}
                onClose={resetForm}
                onSave={handleSave}
                title={editMode ? 'Edit Task' : 'Add New Task'}
                closeLabel="Cancel"
                saveLabel={editMode ? 'Update' : 'Create'}
                loading={saving}
                width="560px"
            >
                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            Title <span className="text-red-500">*</span>
                        </label>
                        <Input
                            value={formData.title}
                            onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
                            placeholder="Task title"
                            className={cn("w-full", errors.title && "border-red-500")}
                        />
                        {errors.title && <p className="text-red-500 text-xs">{errors.title}</p>}
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Description</label>
                        <Textarea
                            value={formData.description}
                            onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                            placeholder="Optional description"
                            rows={3}
                            className="w-full"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                Assign To <span className="text-red-500">*</span>
                            </label>
                            <Select
                                value={formData.assigned_to_id || undefined}
                                onChange={val => setFormData(p => ({ ...p, assigned_to_id: val }))}
                                placeholder="Select user"
                                className="w-full"
                                status={errors.assigned_to_id ? 'error' : ''}
                                options={users.map(u => ({
                                    value: u.id,
                                    label: `${u.login_fname} ${u.login_lname || ''}`.trim()
                                }))}
                            />
                            {errors.assigned_to_id && <p className="text-red-500 text-xs">{errors.assigned_to_id}</p>}
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Due Date</label>
                            <DatePicker
                                value={formData.due_date ? dayjs(formData.due_date) : null}
                                onChange={val => setFormData(p => ({ ...p, due_date: val }))}
                                className="w-full"
                                format="DD/MM/YYYY"
                                placeholder="Select date"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Priority</label>
                            <Select
                                value={formData.priority}
                                onChange={val => setFormData(p => ({ ...p, priority: val }))}
                                className="w-full"
                                options={[
                                    { value: 'Low', label: 'Low' },
                                    { value: 'Medium', label: 'Medium' },
                                    { value: 'High', label: 'High' },
                                ]}
                            />
                        </div>

                        {editMode && (
                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Status</label>
                                <Select
                                    value={formData.task_status}
                                    onChange={val => setFormData(p => ({ ...p, task_status: val }))}
                                    className="w-full"
                                    options={[
                                        { value: 'Pending', label: 'Pending' },
                                        { value: 'In Progress', label: 'In Progress' },
                                        { value: 'Completed', label: 'Completed' },
                                    ]}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </DialogModal>

            {/* Delete Confirm */}
            <ConfirmPopupModal
                open={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                onConfirm={handleDelete}
                title="Delete Task"
                description="Are you sure you want to delete this task?"
                type="danger"
                confirmLabel="Delete"
                destructive={true}
            />
        </ContainerCard>
    );
};

export default TasksList;
